import React, { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw } from 'lucide-react';

type InviteCode = {
  id: string;
  code: string;
  created_by: string | null;
  is_active: boolean;
  used_by: string | null;
  used_at: string | null;
  created_at: string;
};

const CodesTab = () => {
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const { toast } = useToast();

  React.useEffect(() => {
    fetchInviteCodes();
    
    // Subscribe to real-time updates
    const channel = supabase
      .channel('invite_codes_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'invite_codes' 
        }, 
        () => {
          fetchInviteCodes();
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchInviteCodes = async () => {
    try {
      setIsLoading(true);
      const { data, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: codes, error } = await supabase
        .from('invite_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setInviteCodes(codes || []);
    } catch (error: any) {
      console.error('Error fetching invite codes:', error);
      toast({
        title: 'Fehler',
        description: 'Fehler beim Laden der Einladungscodes.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateInviteCode = async () => {
    const { data, error: userError } = await supabase.auth.getUser();
    if (userError) {
      toast({
        title: 'Fehler',
        description: 'Fehler beim Abrufen des Benutzers.',
        variant: 'destructive',
      });
      return;
    }
    
    const userId = data?.user?.id;
    if (!userId) {
      toast({
        title: 'Fehler',
        description: 'Benutzer nicht gefunden.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsGeneratingCode(true);
    
    try {
      const { data: responseData, error } = await supabase.rpc('generate_invite_code', {
        user_id: userId
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Code generiert',
        description: `Neuer Einladungscode: ${responseData}`,
      });
      
      fetchInviteCodes();
      
    } catch (error: any) {
      console.error('Error generating invite code:', error);
      toast({
        title: 'Fehler',
        description: error.message || 'Fehler bei der Generierung des Einladungscodes.',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Kopiert',
      description: 'In die Zwischenablage kopiert.',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Einladungscodes</CardTitle>
          <CardDescription>
            Verwalten Sie Einladungscodes für den Beta-Zugang.
          </CardDescription>
        </div>
        <Button 
          onClick={generateInviteCode}
          disabled={isGeneratingCode}
          className="bg-jade hover:bg-jade-light"
        >
          {isGeneratingCode ? (
            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
          ) : null}
          Neuen Code generieren
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-10">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-jade" />
            <p>Codes werden geladen...</p>
          </div>
        ) : inviteCodes.length === 0 ? (
          <Alert>
            <AlertDescription>
              Derzeit sind keine Einladungscodes vorhanden.
            </AlertDescription>
          </Alert>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Erstellt am</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Verwendet von</TableHead>
                <TableHead>Verwendet am</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inviteCodes.map((code) => (
                <TableRow key={code.id}>
                  <TableCell className="font-medium font-mono">{code.code}</TableCell>
                  <TableCell>{formatDate(code.created_at)}</TableCell>
                  <TableCell>
                    {code.is_active ? (
                      <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">
                        Aktiv
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-gray-500/10 text-gray-500 border-gray-500/30">
                        Verwendet
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{code.used_by ? 'Ja' : '-'}</TableCell>
                  <TableCell>{code.used_at ? formatDate(code.used_at) : '-'}</TableCell>
                  <TableCell>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => copyToClipboard(code.code)}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Kopieren
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default CodesTab;
